import { tp } from '../../../i18n/useP';
// Revenue presets for the Billing tab.
//
// Each preset exposes `generate(params)` returning an array of 12 integers
// (monthly amounts, HT, CHF). Generated values are pure functions of params —
// no side effects. Users always edit cells freely after applying a preset.
//
// Rounding rule: when a preset distributes a total across months, integer
// rounding may leave a residue. The residue is added to the last non-zero
// month so the final sum matches the requested total exactly.

const MONTHS_ALL = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

function zeros() {
  return Array(12).fill(0);
}

function distributeWithResidue(total, months) {
  const values = zeros();
  if (!months.length) return values;
  const baseShare = Math.round(total / months.length);
  months.forEach((m) => { values[m] = baseShare; });
  const diff = total - baseShare * months.length;
  if (diff !== 0) values[months[months.length - 1]] += diff;
  return values;
}

// ── 1. Recurring (abonnement / mandat fixe) ──────────────────────────────
function generateRecurring({ monthlyAmount = 5000 } = {}) {
  const v = Math.round(monthlyAmount);
  return Array(12).fill(v);
}

// ── 2. Time & material (régie) ───────────────────────────────────────────
function generateTimeAndMaterial() {
  return zeros();
}

// ── 3. Hour bank (carnet d'heures) ───────────────────────────────────────
function generateHourBank({ totalAmount = 60000, months = [0, 1, 2, 3, 4, 5] } = {}) {
  const cleanMonths = (months || []).filter((m) => Number.isInteger(m) && m >= 0 && m <= 11);
  return distributeWithResidue(Math.round(totalAmount), cleanMonths);
}

// ── 4. Quarterly billing (échéancier trimestriel) ────────────────────────
function generateQuarterlyBilling({ quarterlyAmount = 15000, months = [2, 5, 8, 11] } = {}) {
  const values = zeros();
  const cleanMonths = (months || []).filter((m) => Number.isInteger(m) && m >= 0 && m <= 11);
  const amount = Math.round(quarterlyAmount);
  cleanMonths.forEach((m) => { values[m] = amount; });
  return values;
}

// ── 5. Training sessions (formations ponctuelles) ────────────────────────
function generateTraining({ sessions = [{ month: 4, amount: 20000 }, { month: 8, amount: 20000 }, { month: 10, amount: 20000 }] } = {}) {
  const values = zeros();
  (sessions || []).forEach(({ month, amount }) => {
    if (Number.isInteger(month) && month >= 0 && month <= 11) {
      values[month] += Math.round(Number(amount) || 0);
    }
  });
  return values;
}

// ── 6. Fixed-price project (acomptes %) ──────────────────────────────────
function generateFixedPrice({ totalAmount = 100000, milestones = [{ month: 0, pct: 30 }, { month: 3, pct: 40 }, { month: 6, pct: 30 }] } = {}) {
  const values = zeros();
  const list = (milestones || []).filter(
    ({ month }) => Number.isInteger(month) && month >= 0 && month <= 11
  );
  const total = Math.round(totalAmount);
  let allocated = 0;
  list.forEach(({ month, pct }, idx) => {
    const raw = total * (Number(pct) || 0) / 100;
    const rounded = idx === list.length - 1 ? total - allocated : Math.round(raw);
    values[month] += rounded;
    allocated += rounded;
  });
  return values;
}

// ── 7. License / royalty (annual prepaid, one-month hit) ─────────────────
function generateLicenseAnnual({ annualAmount = 24000, paymentMonth = 0 } = {}) {
  const values = zeros();
  const m = Number.isInteger(paymentMonth) && paymentMonth >= 0 && paymentMonth <= 11
    ? paymentMonth
    : 0;
  values[m] = Math.round(annualAmount);
  return values;
}

// ── 8. Variable (commissions, apporteur — pure free entry, like T&M) ─────
function generateVariable() {
  return zeros();
}

// ── 9. Seasonal (saisonnier — factors renormalized to annual total) ──────
const SEASONAL_PATTERNS = {
  q4_peak:    [0.85, 0.90, 0.95, 0.95, 0.95, 0.95, 0.85, 0.85, 1.05, 1.15, 1.25, 1.30],
  summer_dip: [1.05, 1.05, 1.10, 1.10, 1.05, 0.90, 0.70, 0.70, 1.05, 1.15, 1.15, 1.00],
  ramp:       [0.60, 0.70, 0.80, 0.85, 0.90, 0.95, 1.00, 1.05, 1.10, 1.20, 1.35, 1.50],
};

function generateSeasonal({ annualAmount = 120000, pattern = 'q4_peak' } = {}) {
  const factors = SEASONAL_PATTERNS[pattern] || SEASONAL_PATTERNS.q4_peak;
  const sumFactors = factors.reduce((a, b) => a + b, 0);
  const total = Math.round(annualAmount);
  const values = factors.map((f) => Math.round(total * f / sumFactors));
  const diff = total - values.reduce((a, b) => a + b, 0);
  if (diff !== 0) values[11] += diff;
  return values;
}

// ── Registry exported to the UI ──────────────────────────────────────────
// labelFr kept as property name for callers; value is translated via tp().
export const PRESETS = [
  {
    id: 'recurring',
    labelFr: tp('phrases:p_0f06ce622a'),
    description: tp('phrases:p_8f77509eb0'),
    defaultParams: { monthlyAmount: 5000 },
    generate: generateRecurring,
  },
  {
    id: 'timeAndMaterial',
    labelFr: tp('phrases:p_b61feed78b'),
    description: tp('phrases:p_61a3f783a6'),
    defaultParams: {},
    generate: generateTimeAndMaterial,
  },
  {
    id: 'hourBank',
    labelFr: tp('phrases:p_a5479753e0'),
    description: tp('phrases:p_89b2d48974'),
    defaultParams: { totalAmount: 60000, months: MONTHS_ALL.slice(0, 6) },
    generate: generateHourBank,
  },
  {
    id: 'quarterlyBilling',
    labelFr: tp('phrases:p_0de09d5391'),
    description: tp('phrases:p_cd291de08e'),
    defaultParams: { quarterlyAmount: 15000, months: [2, 5, 8, 11] },
    generate: generateQuarterlyBilling,
  },
  {
    id: 'training',
    labelFr: tp('phrases:p_d65960e750'),
    description: tp('phrases:p_7b413fd1ae'),
    defaultParams: { sessions: [{ month: 4, amount: 20000 }, { month: 8, amount: 20000 }, { month: 10, amount: 20000 }] },
    generate: generateTraining,
  },
  {
    id: 'fixedPrice',
    labelFr: tp('phrases:p_15d4bd71da'),
    description: tp('phrases:p_5ba399d6ab'),
    defaultParams: { totalAmount: 100000, milestones: [{ month: 0, pct: 30 }, { month: 3, pct: 40 }, { month: 6, pct: 30 }] },
    generate: generateFixedPrice,
  },
  {
    id: 'licenseAnnual',
    labelFr: tp('phrases:p_c80086e2db'),
    description: tp('phrases:p_4e6c67beb9'),
    defaultParams: { annualAmount: 24000, paymentMonth: 0 },
    generate: generateLicenseAnnual,
  },
  {
    id: 'variable',
    labelFr: tp('phrases:p_bfb204a1cc'),
    description: tp('phrases:p_46028e0456'),
    defaultParams: {},
    generate: generateVariable,
  },
  {
    id: 'seasonal',
    labelFr: tp('phrases:p_5884c4ccfe'),
    description: tp('phrases:p_1cbf692716'),
    defaultParams: { annualAmount: 120000, pattern: 'q4_peak' },
    generate: generateSeasonal,
  },
];

export const PRESETS_BY_ID = Object.fromEntries(PRESETS.map((preset) => [preset.id, preset]));

export function applyPreset(presetId, params) {
  const preset = PRESETS_BY_ID[presetId];
  if (!preset) return zeros();
  return preset.generate(params || preset.defaultParams);
}

export { SEASONAL_PATTERNS };
